import type { MetadataEvent } from "../metadata-history/metadata-events";
import type { MetadataTargetIdentity } from "../metadata-history/metadata-targets";
import type { ScheduleEvent } from "./history-events";

export type AppHistoryAction =
  | { domain: "schedule"; event: ScheduleEvent }
  | { domain: "metadata"; sessionId: string; event: MetadataEvent; target?: MetadataTargetIdentity };

export type HistoryScope = "schedule" | { sessionId: string };

function matchesScope(action: AppHistoryAction, scope?: HistoryScope) {
  if (!scope) return true;
  if (scope === "schedule") return action.domain === "schedule";
  return action.domain === "metadata" && action.sessionId === scope.sessionId;
}

export function nextUndo(events: AppHistoryAction[], undoneIds: string[], scope?: HistoryScope) {
  return events.findLast(action => !undoneIds.includes(action.event.id) && matchesScope(action, scope));
}

export function nextRedo(events: AppHistoryAction[], undoneIds: string[], scope?: HistoryScope) {
  // Redo reverses undo order, including scoped undos that left gaps in the timeline.
  const id = undoneIds.findLast(id => {
    const action = events.find(action => action.event.id === id);
    return action !== undefined && matchesScope(action, scope);
  });
  return events.find(action => action.event.id === id);
}

export function chronologicalCursor(events: AppHistoryAction[], undoneIds: string[]) {
  const firstUndone = events.findIndex(action => undoneIds.includes(action.event.id));
  if (firstUndone === -1) return events.length;
  const hasAppliedAfter = events.slice(firstUndone).some(action => !undoneIds.includes(action.event.id));
  return hasAppliedAfter ? null : firstUndone;
}

export function canMergeAction(last: AppHistoryAction | undefined, action: AppHistoryAction, sealed: boolean) {
  if (sealed || !last || !action.event.mergeKey) return false;
  if (last.domain !== action.domain) return false;
  if (last.domain === "metadata" && action.domain === "metadata" && last.sessionId !== action.sessionId) return false;
  if (last.event.type !== action.event.type) return false;
  if (last.event.mergeKey !== action.event.mergeKey) return false;
  return action.event.at - last.event.at <= 700;
}

export function mergeAction(last: AppHistoryAction, action: AppHistoryAction): AppHistoryAction {
  const id = last.event.id;
  if (last.domain === "schedule" && action.domain === "schedule") {
    const previous = last.event;
    const event = action.event;
    if (previous.type === "slot/set" && event.type === "slot/set") {
      return { ...action, event: { ...event, id, patch: { ...previous.patch, ...event.patch } } };
    }
    if (previous.type === "offline/set" && event.type === "offline/set") {
      return { ...action, event: { ...event, id, patch: { ...previous.patch, ...event.patch } } };
    }
  }
  if (action.domain === "schedule") return { ...action, event: { ...action.event, id } };
  return { ...action, event: { ...action.event, id } };
}
